'use client';
import React from 'react';
import { Input } from 'antd';
import styled from '@benzinga/themetron';
import { SessionContext } from '@benzinga/session-context';
import { StockSymbol } from '@benzinga/session';
import { Note, NotesManager } from '@benzinga/notes-manager';
import { isString } from '@benzinga/utils';
import { TickerContext } from '../TickerContext';
import { TextAreaRef } from 'antd/lib/input/TextArea';
import { TrackingManager } from '@benzinga/tracking-manager';

interface NotesState {
  newText: string;
  notes: Note[];
  placeholder: string;
}

interface Props {
  symbol: StockSymbol;
}

export const TickerNotes: React.FC<Props> = props => {
  const session = React.useContext(SessionContext);
  const ticker = React.useContext(TickerContext);
  const [notesState, setNotesState] = React.useState<NotesState>({
    newText: '',
    notes: [],
    placeholder: `Type a new note...`,
  });
  const textAreaRef = React.useRef<TextAreaRef>(null);
  const addNoteButtonJustClicked = React.useRef<boolean>(false);
  const [isAddNoteFocused, setIsAddNoteFocused] = React.useState<boolean>(false);

  React.useEffect(() => {
    (async () => {
      const notesManager = session.getManager(NotesManager);
      const { ok: notes = [] } = await notesManager.getNotesFiltered(props.symbol);
      setNotesState(oldState => ({
        ...oldState,
        notes,
      }));
    })();
  }, [session, props.symbol]);

  React.useEffect(() => {
    if (isAddNoteFocused && textAreaRef.current) {
      textAreaRef.current.focus();
    }
  }, [isAddNoteFocused]);

  const onFocus = () => {
    setIsAddNoteFocused(true);
    ticker.setDialogMode(true);
  };

  const onBlur = () => {
    if (addNoteButtonJustClicked.current) {
      addNoteButtonJustClicked.current = false;
      if (isAddNoteFocused && textAreaRef.current) {
        textAreaRef.current.focus();
      }
    }
    setIsAddNoteFocused(false);
    ticker.setDialogMode(false);
  };

  const onNewNoteChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e?.target?.value;
    addNoteButtonJustClicked.current = true;
    if (isString(val)) {
      setNotesState(oldState => ({ ...oldState, newText: val }));
      ticker.updateHeight();
    }
  };

  const onNewNote = async () => {
    const notesManager = session.getManager(NotesManager);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    setNotesState(oldState => {
      if (oldState.newText.length > 0) {
        (async () => {
          await notesManager.createNote(props.symbol, notesState.newText);
          const { ok: notes = [] } = await notesManager.getNotesFiltered(props.symbol);
          session.getManager(TrackingManager).trackNoteEvent('add', {
            symbol: props.symbol,
          });
          setNotesState(oldState => ({
            ...oldState,
            notes,
          }));
          ticker.updateHeight();
        })();
        return {
          ...oldState,
          newText: '',
          placeholder: `Type a new note...`,
        };
      } else {
        ticker.updateHeight();
        return {
          ...oldState,
          placeholder: 'Note cannot be empty',
        };
      }
    });
  };

  return (
    <>
      <NoteHeaderDiv>
        <LatestNotesDiv>Latest Note</LatestNotesDiv>
      </NoteHeaderDiv>
      {notesState.notes?.[0]?.getText()?.length > 0 && <NoteBodyDiv>{notesState.notes?.[0]?.getText()}</NoteBodyDiv>}
      <NotesAddDiv>
        <NoteAddBorderDiv>
          <RowContainerDiv>
            {isAddNoteFocused ? (
              <Input.TextArea
                autoSize={true}
                onBlur={onBlur}
                onChange={onNewNoteChange}
                onFocus={onFocus}
                placeholder={notesState.placeholder}
                ref={textAreaRef}
                value={notesState.newText}
              />
            ) : (
              <Input
                onBlur={onBlur}
                onFocus={onFocus}
                placeholder={notesState.placeholder}
                value={notesState.newText}
              />
            )}
            <AddButtonContainerDiv>
              <AddButtonDiv onClick={onNewNote}>Add</AddButtonDiv>
            </AddButtonContainerDiv>
          </RowContainerDiv>
        </NoteAddBorderDiv>
      </NotesAddDiv>
    </>
  );
};

const NoteHeaderDiv = styled.div``;

const NoteBodyDiv = styled.div`
  margin: 5px 12px;
  max-width: 350px;
  white-space: normal;
`;

const LatestNotesDiv = styled.div`
  font-size: 1.2em;
  margin: 5px 12px;
`;

const NotesAddDiv = styled.div`
  margin-top: 2px;
`;

const NoteAddBorderDiv = styled.div`
  border-bottom-width: 1px;
  border-color: ${props => props.theme.colors.border};
  border-style: solid;
  margin-bottom: 5px;
  padding: 5px;
`;

const RowContainerDiv = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const AddButtonDiv = styled.div`
  background: ${props => props.theme.colors.brand};
  cursor: pointer;
  margin-bottom: 3px;
  margin-left: 4px;
  padding-bottom: 6px;
  padding-left: 10px;
  padding-right: 10px;
  padding-top: 6px;
`;

const AddButtonContainerDiv = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
`;
